import BuildShowStore from "~/stores/BuildShowStore";
import { Build, Job, CommandJob } from "~/components/build/Show/lib/types";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { dropRight, filter, isNumber, matchesProperty, noop } from "lodash";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { QueryObserver, useQueryClient } from "@tanstack/react-query";
import { stepsQueryKey, useStepsQuery } from "~/components/build/Show/lib/useSteps";
import { Step } from "~/lib/pipeline";

const BuildContext = createContext<NullableBuildContext>({
  build: null,
  store: null,
  setBuild: noop,
});

export const BuildContextProvider = ({ children, store }) => {
  const [build, setBuild] = useState(store?.build);
  const setSteps = useBuildStore((state) => state.setSteps);
  const setJobs = useBuildStore((state) => state.setJobs);
  const queryClient = useQueryClient();

  // Fetch the steps
  const stepsQuery = useStepsQuery(build);

  /**
   * Hook into query observer to ensure we always have the latest steps.
   */
  useEffect(() => {
    if (!Features.ProgressiveBuildShow) {
      return;
    }

    const observer = new QueryObserver<Step[]>(queryClient, { queryKey: stepsQueryKey(build) });
    const unsubscribe = observer.subscribe((result) => {
      if (result.data) {
        setSteps(result.data);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  /**
   * Fallback to referencing steps and jobs directly from the build.
   */
  useEffect(() => {
    if (!Features.ProgressiveBuildShow) {
      setSteps(build.steps);
      setJobs(build.jobs);
    }
  }, [build]);

  useEffect(() => {
    store?.on("change", () => {
      setBuild(store?.build);

      // todo: refetch only the steps that have changed
      if (Features.ProgressiveBuildShow) {
        stepsQuery.refetch();
      }
    });
    return () => {
      store?.off("change");
    };
  }, []);

  const loadAndEmit = useCallback(
    (build: Build) => {
      store.loadAndEmit(build);
    },
    [store],
  );

  return (
    <BuildContext.Provider value={{ build, setBuild: loadAndEmit, store }}>
      {children}
    </BuildContext.Provider>
  );
};

type StrictBuildContext = {
  build: Build;
  store: BuildShowStore;
  setBuild: (build: Build) => void;
};

type NullableBuildContext = {
  build: (Build & Record<string, unknown>) | null;
  store: BuildShowStore | null;
  setBuild: (build: Build) => void;
};

// Overload signatures
export function useBuild(): StrictBuildContext;
export function useBuild(options: { strict: false }): NullableBuildContext;

// Implementation
export function useBuild(options?: {
  strict?: boolean;
}): StrictBuildContext | NullableBuildContext {
  const context = useContext(BuildContext);

  if (context === undefined) {
    throw new Error("useBuild() must be used within a <BuildContextProvider />");
  }

  if (options?.strict !== false && (!context.build || !context.store)) {
    throw new Error("Build context is missing required build or store");
  }

  return context;
}

/**
 * Fetch the most recent parallel jobs for a given step.
 */
export const useMostRecentParallelJobsForStep = (stepUuid: string): CommandJob[] => {
  const jobs = useJobsForStep<CommandJob>(stepUuid);

  return mostRecentParallelJobs(jobs);
};

/**
 * Fetch the most recent matrix jobs for a given step.
 */
export const useMostRecentMatrixJobsForStep = (stepUuid: string): CommandJob[] => {
  const jobs = useJobsForStep<CommandJob>(stepUuid);

  return mostRecentMatrixJobs(jobs);
};

export function useJobsForStep<T extends Job>(stepUuid: string): T[] {
  const { store } = useBuild({ strict: false });

  if (!store) {
    return [];
  }
  return (store.stepJobs.get(stepUuid) as T[]) || [];
}

export function useMostRecentJob<T extends Job>(stepId: string): T | null {
  const jobs = useJobsForStep<T>(stepId);
  if (!jobs.length) {
    return null;
  }
  return jobs[jobs.length - 1];
}

export function findJobsForStep<T extends Job>(build: Build, stepUuid: string): T[] {
  return build.jobs.filter((job) => job.stepUuid === stepUuid) as T[];
}

export function mostRecentParallelJobs(jobs: CommandJob[]): CommandJob[] {
  return Array.from(
    jobs
      .reduce((acc, job) => {
        acc.set(job.parallelGroupIndex, job);
        return acc;
      }, new Map())
      .values(),
  );
}

export function mostRecentMatrixJobs(jobs: CommandJob[]): CommandJob[] {
  return Array.from(
    jobs
      .reduce((acc, job) => {
        if (!job.matrix) {
          return acc;
        }

        acc.set(JSON.stringify(job.matrix), job);
        return acc;
      }, new Map())
      .values(),
  );
}

type UseRetriesForJobOptions = {
  includeLatest?: boolean;
  jobsForStep?: CommandJob[];
};

export function useRetriesForJob(
  job: CommandJob,
  { includeLatest = true, jobsForStep }: UseRetriesForJobOptions = {},
): CommandJob[] {
  let jobs = jobsForStep || useJobsForStep<CommandJob>(job.stepUuid);

  if (job.matrix) {
    jobs = filter(jobs, matchesProperty("matrix", job.matrix));
  }

  if (isNumber(job.parallelGroupIndex)) {
    jobs = filter(jobs, { parallelGroupIndex: job.parallelGroupIndex });
  }

  if (!includeLatest) {
    return dropRight(jobs, 1);
  }

  return jobs;
}

export default BuildContext;
